import * as Yup from 'yup';
import { Address } from '@synergy/models';
import { Field, FieldProps, Formik, FormikProps } from 'formik';
import { FormHelperText, Grid } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import { PoolButton, PoolModal, PoolSelect, PoolTextField, SelectOption } from '@synergy/components';
import { fields, getLocalizedString } from '@synergy/utils';

import { DemandAccountItem } from '../../../../../models/demand-account-item';
import { DemandItem } from '../../../../../models/demand-item';
import {
    PartialAddress,
    PoolAccountAddressAutocompleteList,
} from '../../../../../../../common/components/autocomplete-list/account-address-autocomplete-list/pool-account-address-autocomplete-list';
import { demandDetailPositionsDataSelector } from '../../../store/positions/demand-detail-positions-selectors';
import { useAppSelector } from '../../../../../../../store/use-appstate-selector';

interface FeedbackDetailNewPositionModalProps {
    isVisible: boolean;
    accountId: number;
    onClose: () => void;
    onAddDemandItem: (demandAccountItem: DemandAccountItem) => void;
}

export interface CustomDemandItem {
    deliveryAddress: Address | null;
    costCentre?: string;
    demandItem: DemandItem | null;
    quantity: number;
}

const fieldNames = fields<CustomDemandItem>();

export const FeedbackDetailNewPositionModal = (props: FeedbackDetailNewPositionModalProps) => {
    const { isVisible, onClose, onAddDemandItem, accountId } = props;
    const locales = useAppSelector(state => state.supportedLocales);
    const allDemandItems = useAppSelector(demandDetailPositionsDataSelector).values.demandItems;
    const intl = useIntl();

    const validationSchema = Yup.object().shape({
        deliveryAddress: Yup.mixed().required('app.error.required'),
        demandItem: Yup.mixed().required('app.error.required'),
        quantity: Yup.number().moreThan(0, 'app.error.greaterZero').required('app.error.required'),
    });

    const handleSubmit = (customDemandItem: CustomDemandItem) => {
        if (
            customDemandItem?.demandItem?.id &&
            customDemandItem.quantity &&
            customDemandItem.deliveryAddress &&
            onAddDemandItem
        ) {
            const demandAccountItem: DemandAccountItem = {
                deliveryAddress: { ...customDemandItem.deliveryAddress },
                productNumber: customDemandItem.demandItem.productNumber,
                name: customDemandItem.demandItem.name,
                quantity: customDemandItem.quantity,
                product: { id: customDemandItem.demandItem.id ?? 0 },
                demandItemId: customDemandItem.demandItem.id,
                costCentre: customDemandItem.costCentre,
            };

            onAddDemandItem(demandAccountItem);

            onClose?.();
        }
    };

    const handleItemSelected = (form: FormikProps<CustomDemandItem>, name: string) => (value?: number) => {
        if (value) {
            const item = allDemandItems.find(demandItem => demandItem.id === value);
            if (item) {
                form.setFieldValue(name, item);
            }
        }
    };

    const demandItemsOptions: SelectOption[] = allDemandItems
        .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
        .map((demandItem, index) => ({
            value: demandItem.id,
            displayValue: `${intl.formatMessage({ id: 'app.table.column.positionNumber' })} ${
                (demandItem.position || index) + 1
            }: ${getLocalizedString(locales, demandItem.name)}`,
        }));

    return (
        <Formik
            onSubmit={handleSubmit}
            enableReinitialize={true}
            initialValues={{ quantity: 1, deliveryAddress: null, demandItem: null, costCentre: undefined }}
            validationSchema={validationSchema}
        >
            {form => (
                <PoolModal
                    title={<FormattedMessage id="app.demands.detail.feedback.showDetails.addPosition" />}
                    handleClose={onClose}
                    isVisible={isVisible}
                    actionChildren={[
                        <PoolButton key="1" variant="text" styleType="firstLevel" onClick={onClose}>
                            <FormattedMessage id="app.common.cancel" />
                        </PoolButton>,
                        <PoolButton key="2" styleType="firstLevel" onClick={() => form.submitForm()}>
                            <FormattedMessage id="app.common.add" />
                        </PoolButton>,
                    ]}
                    width="lg"
                    sx={{ content: { minHeight: 380 } }}
                >
                    <Grid item={true} container={true} spacing={2}>
                        <Grid item={true} xs={12}>
                            <Field name={fieldNames.deliveryAddress}>
                                {({ form, field, meta }: FieldProps<PartialAddress>) => (
                                    <div>
                                        <PoolAccountAddressAutocompleteList
                                            accountId={accountId}
                                            autocompleteType="DeliveryAddress"
                                            required={true}
                                            onSelected={value => {
                                                form.setFieldValue(field.name, value);
                                                form.setFieldValue(fieldNames.costCentre, value?.costCentre);
                                            }}
                                            error={!!meta.error && meta.touched}
                                            {...field}
                                        />
                                        {meta.error && meta.touched && (
                                            <FormHelperText error={true}>
                                                <FormattedMessage id={meta.error} />
                                            </FormHelperText>
                                        )}
                                    </div>
                                )}
                            </Field>
                        </Grid>
                        <Grid item={true} xs={9}>
                            <Field name={fieldNames.demandItem}>
                                {({ form, field, meta }: FieldProps<DemandItem>) => (
                                    <div>
                                        <PoolSelect
                                            fullWidth={true}
                                            options={demandItemsOptions}
                                            disallowDefaultValue={true}
                                            value={field.value && field.value.id}
                                            onValueChange={handleItemSelected(form, field.name)}
                                            name={field.name}
                                            label={<FormattedMessage id="app.label.product" />}
                                            required={true}
                                            error={!!meta.error && meta.touched}
                                        />
                                        {meta.error && meta.touched && (
                                            <FormHelperText error={true}>
                                                <FormattedMessage id={meta.error} />
                                            </FormHelperText>
                                        )}
                                    </div>
                                )}
                            </Field>
                        </Grid>
                        <Grid item={true} xs={3}>
                            <Field name={fieldNames.quantity}>
                                {({ form, field, meta }: FieldProps<string>) => (
                                    <PoolTextField
                                        {...field}
                                        fullWidth={true}
                                        required={true}
                                        error={meta.touched && !!meta.error}
                                        helperText={
                                            meta.touched && meta.error ? <FormattedMessage id={meta.error} /> : null
                                        }
                                        label={(form.values.demandItem && form.values.demandItem.orderUnit) || ''}
                                        isNumeric={true}
                                    />
                                )}
                            </Field>
                        </Grid>
                    </Grid>
                </PoolModal>
            )}
        </Formik>
    );
};
